import { Job } from "@/models/Job.model";
import { Text, Flex, Button, Callout } from "@radix-ui/themes";
import { FC, JSX } from "react";
import DateObject from "react-date-object";
import { useCurrentUserProfile } from "@/store/auth/userProfile.store";
import { ProfileJobJunction } from "@/models/ProfileJobJunction";

type JobApplyProps = Required<Pick<Job, "id" | "application_deadline">> & {
  loading: boolean;
  error: Error | null;
  handleApply: (job_id: string, uid: string) => Promise<void>;
  data: ProfileJobJunction | null;
  applicationStatus: boolean;
  applicationError: Error | null;
  refetch: () => void;
};

const JobSingleUIApply: FC<JobApplyProps> = ({
  id,
  application_deadline,
  loading,
  error,
  handleApply,
  data,
  applicationStatus,
  applicationError,
  refetch,
}): JSX.Element => {
  const user = useCurrentUserProfile((s) => s.user);
  // Dates
  const applicationDeadline = new DateObject(application_deadline.toDate());
  const today = new DateObject();
  const pastDeadline = applicationDeadline <= today;

  const handleApplyClick = async (job_id: string, uid: string) => {
    await handleApply(job_id, uid);
    refetch();
  };

  if (applicationStatus) {
    return (
      <Flex direction="column" gap="3">
        <Text size="1" color="gray" align="center">
          Checking application status...
        </Text>
      </Flex>
    );
  }

  if (applicationError) {
    return (
      <Flex direction="column" gap="3">
        <Text size="1" color="red">
          {applicationError.message}
        </Text>
      </Flex>
    );
  }

  if (data) {
    const applied = data.status === "applied";
    const interview = data.status === "interviewing";
    const offered = data.status === "offered";
    const rejected = data.status === "rejected";
    const hired = data.status === "hired";

    return (
      <Flex direction="column" gap="3">
        <Callout.Root>
          <Callout.Text align="center">
            <Text size="1">
              {applied &&
                `You have applied to this job on ${new DateObject(
                  data.created_at.toDate()
                ).format("DD.MM.YYYY")}.`}
              {interview && `You are being interviewed for this job.`}
              {offered &&
                `You have been offered this job on ${new DateObject(
                  data.updated_at.toDate()
                ).format("DD.MM.YYYY")}.`}
              {rejected &&
                `We have decided not to move forward with your application.`}
              {hired &&
                ` You have been hired for this job on ${new DateObject(
                  data.updated_at.toDate()
                ).format("DD.MM.YYYY")}.`}
            </Text>
          </Callout.Text>
        </Callout.Root>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="3">
      {!pastDeadline && (
        <Button
          size="2"
          variant="solid"
          onClick={() => handleApplyClick(id, user.uid)}
          disabled={loading}
        >
          {loading ? "Applying..." : "Apply Now"}
        </Button>
      )}
      {error && (
        <Text size="1" color="red">
          {error.message}
        </Text>
      )}
      <Text size="1" color="gray" align="center">
        {pastDeadline
          ? "Application deadline has passed."
          : `
        Application deadline is ${applicationDeadline.format("DD.MM.YYYY")}.`}
      </Text>
    </Flex>
  );
};

export default JobSingleUIApply;
